import React, { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, Check, Plus, Loader2, Building2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { authFetch } from '@/utils/authFetch';
import { API_URL } from '@/config/apiConfig';
import { useAuth } from '@/contexts/AuthContext';

// Real multi-company switcher for production tenants. Lists every company the
// signed-in user is a member of, lets them switch the active company (which the
// whole data layer follows via user.business_id), and lets owners add a new
// company. Fails safe to a static tenant badge while loading or on error.
export default function AccountSwitcher({ fallbackName }) {
  const { isOwner } = useAuth();
  const [companies, setCompanies] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ company_name: '', industry: '' });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const r = await authFetch(`${API_URL}/api/account/companies`, {});
      if (r.ok) {
        const d = await r.json();
        setCompanies(d.companies || []);
      } else {
        setCompanies([]);
      }
    } catch (_) {
      setCompanies([]);
    }
  };
  useEffect(() => { load(); }, []);

  const active = (companies || []).find((c) => c.is_active);
  const name = active?.name || fallbackName || 'Company';
  const owner = typeof isOwner === 'function' ? isOwner() : !!isOwner;

  const doSwitch = async (businessId) => {
    if (switching) return;
    setSwitching(true);
    try {
      const r = await authFetch(`${API_URL}/api/account/switch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ business_id: businessId }),
      });
      if (r.ok) {
        window.location.assign('/app');
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Could not switch company');
        setSwitching(false);
      }
    } catch (_) {
      toast.error('Could not switch company');
      setSwitching(false);
    }
  };

  const createCompany = async () => {
    if (!form.company_name.trim()) { toast.error('Enter a company name'); return; }
    setCreating(true);
    try {
      const r = await authFetch(`${API_URL}/api/account/companies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: form.company_name.trim(),
          industry: form.industry.trim() || null,
        }),
      });
      if (r.ok) {
        const d = await r.json();
        toast.success('Company added');
        setShowAdd(false);
        await doSwitch(d.business_id);
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Could not add company');
        setCreating(false);
      }
    } catch (_) {
      toast.error('Could not add company');
      setCreating(false);
    }
  };

  // Fail-safe: single company (or still loading / errored) → static badge, so
  // the header never breaks and single-company tenants see no new chrome noise.
  if (companies === null || companies.length <= 1) {
    return (
      <Badge
        variant="outline"
        data-testid="tenant-business-badge"
        className="hidden md:flex items-center gap-2 border-primary/30 text-primary font-medium px-3 py-1.5 text-xs rounded-full"
      >
        <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
        {name}
      </Badge>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="account-switcher-trigger"
            className="hidden md:flex items-center gap-2 border border-primary/30 text-primary font-medium px-3 py-1.5 text-xs rounded-full hover:bg-primary/5 transition-colors"
          >
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
            {switching && <Loader2 className="w-3 h-3 animate-spin" />}
            {name}
            <ChevronDown className="w-3 h-3 opacity-60" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="w-72">
          <DropdownMenuLabel className="text-xs text-muted-foreground">Your companies</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {companies.map((c) => (
            <DropdownMenuItem
              key={c.business_id}
              data-testid={`company-option-${c.business_id}`}
              className={`cursor-pointer ${c.is_active ? 'bg-primary/10 font-semibold' : ''}`}
              onClick={() => !c.is_active && doSwitch(c.business_id)}
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{c.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">
                      {c.industry || '\u2014'}{c.setup_pending ? ' \u00b7 setup pending' : ''}
                    </p>
                  </div>
                </div>
                {c.is_active && <Check className="w-4 h-4 text-primary" />}
              </div>
            </DropdownMenuItem>
          ))}
          {owner && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="add-company-btn"
                className="cursor-pointer text-primary"
                onClick={() => setShowAdd(true)}
              >
                <Plus className="w-4 h-4 mr-2" /> Add a company
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="manage-companies-btn"
                className="cursor-pointer"
                onClick={() => window.location.assign('/app/companies')}
              >
                <Building2 className="w-4 h-4 mr-2" /> Manage companies & groups
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="add-company-dialog">
          <DialogHeader>
            <DialogTitle>Add a company</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="company_name">Company name</Label>
              <Input
                id="company_name"
                data-testid="add-company-name"
                value={form.company_name}
                onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))}
                placeholder="Acme Mining Pty Ltd"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="industry">Industry (optional)</Label>
              <Input
                id="industry"
                data-testid="add-company-industry"
                value={form.industry}
                onChange={(e) => setForm((f) => ({ ...f, industry: e.target.value }))}
                placeholder="Mining"
                className="mt-1"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              You'll be the owner of this company. It starts in setup mode so you can connect its data.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)} disabled={creating}>Cancel</Button>
            <Button onClick={createCompany} disabled={creating} data-testid="add-company-submit">
              {creating ? 'Adding\u2026' : 'Add company'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
